import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface PWAInstallContextType {
    canInstallPWA: boolean;
    deferredPrompt: Event | null;
    triggerInstallPrompt: () => Promise<{ outcome: 'accepted' | 'dismissed' | 'already_installed' }>;
}

const PWAInstallContext = createContext<PWAInstallContextType | undefined>(undefined);

export const PWAInstallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
    const [canInstallPWA, setCanInstallPWA] = useState(false);
    const [isAppInstalled, setIsAppInstalled] = useState(false); // To prevent showing prompt if already installed

    useEffect(() => {
        // Check initial install state
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsAppInstalled(true);
            setCanInstallPWA(false);
        }

        const handleBeforeInstallPrompt = (e: Event) => {
            if (isAppInstalled) return; // Don't show prompt if already installed

            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstallPWA(true);
        };

        const handleAppInstalled = () => {
            setIsAppInstalled(true);
            setCanInstallPWA(false);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, [isAppInstalled]);

    const triggerInstallPrompt = async (): Promise<{ outcome: 'accepted' | 'dismissed' | 'already_installed' }> => {
        if (isAppInstalled) {
            return { outcome: 'already_installed' };
        }
        if (!deferredPrompt) {
            return { outcome: 'dismissed' }; // Or throw an error
        }

        (deferredPrompt as any).prompt();
        const { outcome } = await (deferredPrompt as any).userChoice;

        setDeferredPrompt(null); // Prompt can only be used once
        setCanInstallPWA(false); // Hide the button after prompt

        if (outcome === 'accepted') {
            setIsAppInstalled(true);
        }
        return { outcome };
    };

    return (
        <PWAInstallContext.Provider value={{ canInstallPWA: canInstallPWA && !isAppInstalled, deferredPrompt, triggerInstallPrompt }}>
            {children}
        </PWAInstallContext.Provider>
    );
};

export const usePWAInstall = (): PWAInstallContextType => {
    const context = useContext(PWAInstallContext);
    if (context === undefined) {
        throw new Error('usePWAInstall must be used within a PWAInstallProvider');
    }
    return context;
}; 